import { useEffect } from 'react';
import type { ProjectStatus } from '@reels/shared';
import { api } from '../lib/api';
import { watchProject } from '../lib/events';
import * as telegram from '../lib/telegram';
import { useActions } from '../state/actions';
import { useStore } from '../state/store';
import type { State } from '../state/store';
import type { ProjectDetail } from '../lib/types';
import { t } from '../i18n';

export function isProcessing(status: ProjectStatus): boolean {
  return !['draft', 'done', 'failed', 'expired'].includes(status);
}

export function activeProjectIds(state: State): string[] {
  const projects = new Map((state.projects ?? []).map(project => [project.id, project]));
  for (const project of Object.values(state.activity)) projects.set(project.id, project);
  if (state.project) projects.set(state.project.id, state.project);
  return [...projects.values()].filter(project => isProcessing(project.status)).map(project => project.id);
}

/** Mounted in the app shell, so changing tabs or starting a new draft keeps tracking jobs. */
function ProjectWatcher({ id }: { id: string }) {
  const { dispatch } = useStore();
  useEffect(() => {
    let alive = true;
    const update = (patch: Partial<ProjectDetail>): void => {
      if (alive) dispatch({ type: 'activity/update', id, patch });
    };
    const stop = watchProject(id, {
      onMode: mode => dispatch({ type: 'stream/mode', mode }),
      onProgress: stage => {
        if (alive) dispatch({ type: 'activity/update', id, patch: { status: stage.status, progress: stage.progress, stage: stage.stage }, stage });
      },
      onSnapshot: snapshot => {
        if (!alive) return;
        dispatch({ type: 'activity/update', id, patch: snapshot, stage: {
          status: snapshot.status, stage: snapshot.stage ?? snapshot.status, progress: snapshot.progress ?? 0,
        } });
      },
      // Keep the bar on screen until the user chooses to view the result.
      onDone: () => {
        void api.getProject(id).then(snapshot => update(snapshot))
          .catch(() => update({ status: 'done', progress: 100 }));
        telegram.hapticFeedback.notification('success');
      },
      onFailed: data => { update({ status: 'failed', error: data.error }); telegram.hapticFeedback.notification('error'); },
      onError: message => update({ status: 'failed', error: message }),
    });
    return () => { alive = false; stop(); };
  }, [id, dispatch]);
  return null;
}

export function ProjectWatchers() {
  const { state } = useStore();
  return <>{activeProjectIds(state).map(id => <ProjectWatcher key={id} id={id} />)}</>;
}

export function progressProject(state: State) {
  const jobs = Object.values(state.activity);
  return jobs.find(project => isProcessing(project.status))
    ?? jobs.find(project => project.status === 'failed')
    ?? jobs.find(project => state.unreadProjects.includes(project.id));
}

export function MontageProgress() {
  const { state } = useStore();
  const actions = useActions();
  const project = progressProject(state);
  if (!project) return null;
  const done = project.status === 'done';
  const failed = project.status === 'failed';
  const percent = done ? 100 : Math.max(0, Math.min(100, Math.round(project.progress ?? 0)));
  return <aside className="montage-progress" aria-label={t('components.progress.aria')}>
    <div className="col grow">
      <div className="row between">
        <strong className="ellipsis" aria-live="polite">{done ? t('components.progress.done') : failed ? t('components.progress.failed') : t('components.progress.running')}</strong>
        {!failed ? <span className="small muted">{percent}%</span> : null}
      </div>
      <div className="bar" role="progressbar" aria-label={t('components.progress.barAria')} aria-valuemin={0} aria-valuemax={100} aria-valuenow={percent}>
        <span style={{ width: percent + '%' }} />
      </div>
    </div>
    <button type="button" className={done ? 'btn primary' : 'btn'} disabled={(!done && !failed) || state.projectLoading}
      onClick={() => void actions.openProject(project.id)}>{failed ? t('components.progress.details') : t('components.progress.view')}</button>
  </aside>;
}
